/**
 * Status Hooks
 *
 * TanStack Query hooks for status management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import type {
  Status,
  StatusListResponse,
  CreateStatusRequest,
  UpdateStatusRequest,
} from '../types/status';

/** Query keys for status */
export const statusKeys = {
  all: ['status'] as const,
  lists: () => [...statusKeys.all, 'list'] as const,
  detail: (id: string) => [...statusKeys.all, 'detail', id] as const,
};

/**
 * Fetch all status values
 */
export function useStatuses() {
  return useQuery({
    queryKey: statusKeys.lists(),
    queryFn: () => apiFetch<StatusListResponse>('/api/statuses'),
    staleTime: 60_000, // 1 minute
  });
}

/**
 * Fetch single status by ID
 */
export function useStatusDetail(id: string) {
  return useQuery({
    queryKey: statusKeys.detail(id),
    queryFn: () => apiFetch<Status>(`/api/statuses/${id}`),
    enabled: !!id,
  });
}

/**
 * Create new status
 */
export function useCreateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStatusRequest) =>
      apiFetch<Status>('/api/statuses', {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statusKeys.lists() });
    },
  });
}

/**
 * Update existing status
 */
export function useUpdateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateStatusRequest & { id: string }) =>
      apiFetch<Status>(`/api/statuses/${id}`, {
        method: 'PUT',
        body: data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: statusKeys.lists() });
      queryClient.invalidateQueries({ queryKey: statusKeys.detail(variables.id) });
    },
  });
}

/**
 * Delete status
 */
export function useDeleteStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/statuses/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statusKeys.lists() });
    },
  });
}
